import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  inject,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { Router } from '@angular/router';
import { DialogService, DynamicDialogRef } from '../../../core/services/dialog';
import { MatTooltipModule } from '@angular/material/tooltip';
import { CacheService } from '../../../core/services/cache/cache.service';
import { DashboardService } from '../../../core/services/dashboard/dashboard.service';
import {
  LayoutService,
  NavigationMode,
} from '../../../core/services/layout/layout.service';
import { TranslationService } from '../../../core/services/translation/translation.service';
import { environment } from '../../../environments/environment';
import { TranslatePipe } from '../../pipes/translate/translate.pipe';
import { CloseIcon } from '../Icons/ic-close/ic-close';
import { IcUserComponent } from '../Icons/ic-user/ic-user';
import { WorldMapComponent } from '../world-map/world-map.component';
import { KeyboardShortcutsDialogComponent } from './keyboard-shortcuts-dialog/keyboard-shortcuts-dialog.component';
import { RecentDownloadsDialogComponent } from './recent-downloads-dialog/recent-downloads-dialog.component';
import { ThemeSettingsComponent } from './theme-settings/theme-settings.component';

/**
 * UserProfilePopupComponent component.
 * Provides UI presentation and event handling for user profile popup component.
 */
@Component({
  selector: 'app-user-profile-popup',
  standalone: true,
  imports: [
    CommonModule,
    IcUserComponent,
    CloseIcon,
    TranslatePipe,
    WorldMapComponent,
    MatTooltipModule,
  ],
  templateUrl: './user-profile-popup.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./user-profile-popup.component.scss'],
})
export class UserProfilePopupComponent {
  /** Injected service for dialog ref. */
  private dialogRef = inject(DynamicDialogRef);
  /** Injected service for cache service. */
  private cacheService = inject(CacheService);
  /** Injected service for translation service. */
  private translationService = inject(TranslationService);
  /** Injected service for dashboard service. */
  private dashboardService = inject(DashboardService);
  /** Injected service for router. */
  private router = inject(Router);
  /** Injected service for layout service. */
  public layoutService = inject(LayoutService);
  /** Injected service for dialog service. */
  private dialogService = inject(DialogService);

  // User data inputs (defaults if not provided)
  /** Property current user. */
  currentUser = this.cacheService.currentUser;

  /** Property user name. */
  userName = computed(() => {
    const user = this.currentUser();
    return user ? `${user.firstName} ${user.lastName}` : 'Michael Clarke';
  });

  /** Property user email. */
  userEmail = computed(() => {
    const user = this.currentUser();
    return user ? user.email : 'michaelClarke@gmail.com';
  });

  /** Property user role. */
  userRole = computed(() => {
    const user = this.currentUser();
    return user ? user.userType : 'Admin';
  });

  /** Property user initials. */
  userInitials = computed(() => {
    const user = this.currentUser();
    if (!user) return 'MC';
    return `${user.firstName?.charAt(0) || ''}${
      user.lastName?.charAt(0) || ''
    }`.toUpperCase();
  });

  /** Whether the current user has a broker or client company profile to open. */
  hasCompanyProfile = computed(() => this.getCompanyProfileRoute() !== null);

  /** Property app version. */
  appVersion = environment.version;

  /** Property default dashboard. */
  defaultDashboard = computed(() => this.dashboardService.defaultDashboard());

  /** Property module landing page. */
  moduleLandingPage = computed(() => this.dashboardService.moduleLandingPage());

  /** Active UI language code (e.g. `en`, `nl`, `zh`). */
  selectedLanguage = signal(this.translationService.getCurrentLanguageCode());

  /**
   * Switches the active UI language.
   * @param langCode - Translator language code to activate
   */
  selectLanguage(langCode: string): void {
    this.translationService.setActiveLanguage(langCode);
    this.selectedLanguage.set(this.translationService.getCurrentLanguageCode());
  }

  /**
   * Sets the default module dashboard preference.
   * @param dashboardId - Dashboard module id
   */
  setDefaultDashboard(dashboardId: string): void {
    this.dashboardService.setDefaultDashboard(dashboardId);
  }

  /**
   * Sets whether module entry opens the dashboard or list.
   * @param mode - Landing preference
   */
  setModuleLandingPage(mode: 'dashboard' | 'list'): void {
    this.dashboardService.setModuleLandingPage(mode);
  }

  /** Closes the popup and requests a change-password flow. */
  onChangePassword(): void {
    this.dialogRef.close({ action: 'changePassword' });
  }

  /** Closes the popup and requests logout. */
  onLogout(): void {
    this.dialogRef.close({ action: 'logout' });
  }

  /**
   * Resolves the details route for the company associated with the current user.
   * @returns Broker or client details route, or null when no company is available
   */
  private getCompanyProfileRoute(): string[] | null {
    const user = this.currentUser();
    if (!user) return null;

    const companyType = String(user.Company?.type ?? user.userType).toLowerCase();
    const companyId =
      companyType === 'broker'
        ? user.brokerId ?? user.companyId ?? user.Company?.id
        : companyType === 'client'
          ? user.clientId ?? user.companyId ?? user.Company?.id
          : null;

    if (!companyId) return null;

    return [
      `/settings/${companyType === 'broker' ? 'brokers' : 'clients'}/details`,
      String(companyId),
      'info',
    ];
  }

  /** Navigates to the broker or client company profile for the current user. */
  onCompanyProfile(): void {
    const profileRoute = this.getCompanyProfileRoute();
    if (!profileRoute) return;

    void this.router.navigate(profileRoute);
    this.onClose();
  }

  /** Navigates to the current user's profile page. */
  onUserProfile(): void {
    const user = this.currentUser();
    if (!user) return;
    this.router.navigate(['/settings/users/details', user.id]);
    this.onClose();
  }

  /** Opens the theme / language / navigation settings dialog. */
  onThemeSettings(): void {
    setTimeout(() => {
      const themeSettingsRef = this.dialogService.open(ThemeSettingsComponent, {
        width: '550px',
        height: '80dvh',
        contentStyle: { height: '100%', overflow: 'hidden', padding: '0' },
        baseZIndex: 11000,
        dismissableMask: true,
        modal: true,
        closeOnEscape: true,
        closable: true,
        styleClass: 'action-dialog custom-header',
      });

      // Top-bar remounts header chrome; apply mode only after both dialogs close
      // to avoid ExpressionChangedAfterItHasBeenCheckedError (NG0100).
      themeSettingsRef?.onClose.subscribe(
        (result: { applyNavigationMode?: NavigationMode } | undefined) => {
          const mode = result?.applyNavigationMode;
          if (!mode) {
            return;
          }
          this.dialogRef.close();
          setTimeout(() => {
            void this.layoutService.setNavigationMode(mode);
          });
        }
      );
    });
  }

  /** Opens the recent downloads dialog. */
  onRecentDownloads(): void {
    setTimeout(() => {
      this.dialogService.open(RecentDownloadsDialogComponent, {
        width: '450px',
        height: '80dvh',
        contentStyle: { height: '100%', overflow: 'hidden', padding: '0' },
        baseZIndex: 11000,
        dismissableMask: true,
        modal: true,
        closeOnEscape: true,
        closable: true,
        styleClass: 'action-dialog custom-header',
      });
    });
  }

  /** Opens the keyboard shortcuts dialog. */
  onKeyboardShortcuts(): void {
    setTimeout(() => {
      this.dialogService.open(KeyboardShortcutsDialogComponent, {
        width: '450px',
        height: '80dvh',
        contentStyle: { height: '100%', overflow: 'hidden', padding: '0' },
        baseZIndex: 11000,
        dismissableMask: true,
        modal: true,
        closeOnEscape: true,
        closable: true,
        styleClass: 'action-dialog custom-header',
      });
    });
  }

  /** Opens the public API reference docs in a new tab. */
  onViewApiReferenceDocs(): void {
    if (typeof window !== 'undefined') {
      window.open(
        'https://docs.eazecustoms.com/implementation/about-eaze-customs',
        '_blank',
        'noopener,noreferrer'
      );
    }
    this.onClose();
  }

  /** Closes the profile popup. */
  onClose(): void {
    this.dialogRef.close();
  }
}
